import { ChangeDetectionStrategy, Component, computed, ElementRef, OnInit, OnDestroy, signal, viewChild, afterNextRender } from '@angular/core';

interface Bat {
    x: number;
    y: number;
    speed: number;
    amplitude: number;
    frequency: number;
    phase: number;
    scale: number;
    opacity: number;
    wingPhase: number;
    wingSpeed: number;
}

interface FloatingEmber {
    x: number;
    y: number;
    radius: number;
    speed: number;
    drift: number;
    opacity: number;
    flicker: number;
}

@Component({
    selector: 'sphere-halloween-effect',
    standalone: true,
    template: `
        <canvas #halloweenCanvas class="halloween-canvas"></canvas>
        @if (isHalloweenPeriod()) {
            <div class="decorations">
                <div class="decoration top-left">
                    <svg viewBox="0 0 100 100" class="ghost-decoration">
                        <!-- Ghost body -->
                        <path
                            d="M50 20 C35 20 25 30 25 45 L25 70 Q25 75 28 77 L32 75 Q35 72 38 75 L42 77 Q45 75 48 75 Q51 75 54 77 L58 75 Q61 72 64 75 L68 77 Q71 75 75 70 L75 45 C75 30 65 20 50 20 Z"
                            fill="currentColor"
                            opacity="0.12"
                        />
                        <!-- Eyes -->
                        <ellipse cx="42" cy="42" rx="4" ry="6" fill="currentColor" opacity="0.2" />
                        <ellipse cx="58" cy="42" rx="4" ry="6" fill="currentColor" opacity="0.2" />
                        <!-- Mouth -->
                        <ellipse cx="50" cy="55" rx="6" ry="4" fill="currentColor" opacity="0.15" />
                    </svg>
                </div>
                <div class="decoration top-right">
                    <svg viewBox="0 0 100 100" class="pumpkin-face-decoration">
                        <!-- Pumpkin outline -->
                        <ellipse cx="50" cy="50" rx="28" ry="26" fill="currentColor" opacity="0.14" />
                        <!-- Vertical segments -->
                        <path d="M35 30 Q35 50 35 70" stroke="currentColor" stroke-width="1.5" fill="none" opacity="0.08" />
                        <path d="M50 25 Q50 50 50 75" stroke="currentColor" stroke-width="1.5" fill="none" opacity="0.08" />
                        <path d="M65 30 Q65 50 65 70" stroke="currentColor" stroke-width="1.5" fill="none" opacity="0.08" />
                        <!-- Stem -->
                        <rect x="47" y="20" width="6" height="8" rx="2" fill="currentColor" opacity="0.1" />
                        <!-- Jack-o-lantern face -->
                        <polygon points="40,42 44,45 40,48" fill="currentColor" opacity="0.2" />
                        <polygon points="60,42 56,45 60,48" fill="currentColor" opacity="0.2" />
                        <path
                            d="M40 58 Q45 62 50 62 Q55 62 60 58"
                            stroke="currentColor"
                            stroke-width="2"
                            fill="none"
                            opacity="0.18"
                            stroke-linecap="round"
                        />
                    </svg>
                </div>
                <div class="decoration bottom-left">
                    <svg viewBox="0 0 100 100" class="pumpkin-face-decoration">
                        <!-- Pumpkin outline -->
                        <ellipse cx="50" cy="52" rx="26" ry="24" fill="currentColor" opacity="0.13" />
                        <!-- Vertical segments -->
                        <path d="M37 33 Q37 52 37 71" stroke="currentColor" stroke-width="1.5" fill="none" opacity="0.07" />
                        <path d="M50 28 Q50 52 50 76" stroke="currentColor" stroke-width="1.5" fill="none" opacity="0.07" />
                        <path d="M63 33 Q63 52 63 71" stroke="currentColor" stroke-width="1.5" fill="none" opacity="0.07" />
                        <!-- Stem -->
                        <rect x="47" y="24" width="6" height="7" rx="2" fill="currentColor" opacity="0.09" />
                        <!-- Friendly face -->
                        <circle cx="42" cy="46" r="3" fill="currentColor" opacity="0.18" />
                        <circle cx="58" cy="46" r="3" fill="currentColor" opacity="0.18" />
                        <path d="M40 60 Q50 65 60 60" stroke="currentColor" stroke-width="2" fill="none" opacity="0.16" stroke-linecap="round" />
                    </svg>
                </div>
                <div class="decoration bottom-right">
                    <svg viewBox="0 0 100 100" class="ghost-decoration">
                        <!-- Ghost body -->
                        <path
                            d="M50 25 C37 25 28 34 28 47 L28 68 Q28 72 30 74 L33 72 Q36 70 39 72 L42 74 Q44 72 47 72 Q50 72 53 74 L56 72 Q59 70 62 72 L65 74 Q67 72 72 68 L72 47 C72 34 63 25 50 25 Z"
                            fill="currentColor"
                            opacity="0.11"
                        />
                        <!-- Eyes -->
                        <circle cx="43" cy="44" r="3.5" fill="currentColor" opacity="0.18" />
                        <circle cx="57" cy="44" r="3.5" fill="currentColor" opacity="0.18" />
                        <!-- Smile -->
                        <path d="M42 54 Q50 58 58 54" stroke="currentColor" stroke-width="2" fill="none" opacity="0.14" stroke-linecap="round" />
                    </svg>
                </div>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 50;
                overflow: hidden;
            }

            .halloween-canvas {
                width: 100%;
                height: 100%;
                display: block;
            }

            .decorations {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
            }

            .decoration {
                position: absolute;
                opacity: 0;
                animation: fadeInDecoration 2.5s ease-in-out 0.8s forwards;
            }

            .top-left {
                top: 35px;
                left: 35px;
            }
            .ghost-decoration {
                width: 70px;
                height: 70px;
                color: #e8e8e8;
                filter: drop-shadow(0 2px 8px rgba(232, 232, 232, 0.15));
                animation: ghostFloat 6s ease-in-out infinite;
            }

            .pumpkin-face-decoration {
                width: 75px;
                height: 75px;
                color: #ff8c42;
                filter: drop-shadow(0 2px 6px rgba(255, 140, 66, 0.2));
                animation: gentleBob 5s ease-in-out infinite;
            }

            @keyframes gentleBob {
                0%,
                100% {
                    transform: translateY(0) rotate(-1deg);
                }
                50% {
                    transform: translateY(-4px) rotate(1deg);
                }
            }

            @media (max-width: 768px) {
                .decoration {
                    display: none;
                }
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HalloweenEffectComponent implements OnInit, OnDestroy {
    private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('halloweenCanvas');

    private readonly bats = signal<Bat[]>([]);
    private readonly embers = signal<FloatingEmber[]>([]);
    private animationFrameId: number | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private time = 0;

    public readonly isHalloweenPeriod = computed(() => {
        const now = new Date();
        const month = now.getMonth(); // 0-indexed
        const day = now.getDate();

        // October 31st only
        return month === 9 && day === 31;
    });

    constructor() {
        afterNextRender(() => {
            const canvas = this.canvasRef()?.nativeElement;
            if (canvas && this.isHalloweenPeriod()) {
                this.initializeHalloweenEffect(canvas);
            }
        });
    }

    ngOnInit(): void {
        // Initialization handled in afterNextRender
    }

    private initializeHalloweenEffect(canvas: HTMLCanvasElement): void {
        this.ctx = canvas.getContext('2d', { alpha: true });
        if (!this.ctx) return;

        this.resizeCanvas(canvas);
        this.createBats(canvas);
        this.createEmbers(canvas);
        this.animate();

        window.addEventListener('resize', () => this.resizeCanvas(canvas));
    }

    private resizeCanvas(canvas: HTMLCanvasElement): void {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        if (this.ctx) {
            this.createBats(canvas);
            this.createEmbers(canvas);
        }
    }

    private createBats(canvas: HTMLCanvasElement): void {
        const bats: Bat[] = [];
        const numBats = Math.floor((canvas.width * canvas.height) / 50000); // Sparse distribution

        for (let i = 0; i < numBats; i++) {
            bats.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height * 0.6, // Keep bats in upper portion
                speed: Math.random() * 0.3 + 0.2,
                amplitude: Math.random() * 15 + 10,
                frequency: Math.random() * 0.02 + 0.01,
                phase: Math.random() * Math.PI * 2,
                scale: Math.random() * 0.4 + 0.6,
                opacity: Math.random() * 0.15 + 0.1,
                wingPhase: Math.random() * Math.PI * 2,
                wingSpeed: Math.random() * 0.15 + 0.1
            });
        }

        this.bats.set(bats);
    }

    private createEmbers(canvas: HTMLCanvasElement): void {
        const embers: FloatingEmber[] = [];
        const numEmbers = Math.floor((canvas.width * canvas.height) / 15000);

        for (let i = 0; i < numEmbers; i++) {
            embers.push(this.createEmber(canvas, Math.random() * canvas.height));
        }

        this.embers.set(embers);
    }

    private createEmber(canvas: HTMLCanvasElement, yPosition?: number): FloatingEmber {
        return {
            x: Math.random() * canvas.width,
            y: yPosition ?? canvas.height + 10,
            radius: Math.random() * 1.5 + 0.5,
            speed: -(Math.random() * 0.3 + 0.15), // Negative for upward movement
            drift: (Math.random() - 0.5) * 0.3,
            opacity: Math.random() * 0.4 + 0.3,
            flicker: Math.random() * Math.PI * 2
        };
    }

    private drawBat(ctx: CanvasRenderingContext2D, bat: Bat): void {
        ctx.save();
        ctx.globalAlpha = bat.opacity;
        ctx.translate(bat.x, bat.y);
        ctx.scale(bat.scale, bat.scale);

        // Bat body
        ctx.fillStyle = '#2d2d2d';
        ctx.beginPath();
        ctx.ellipse(0, 0, 3, 2, 0, 0, Math.PI * 2);
        ctx.fill();

        // Wings with flapping animation
        const wingFlap = Math.sin(bat.wingPhase) * 0.3;

        ctx.fillStyle = '#1a1a1a';

        // Left wing
        ctx.beginPath();
        ctx.moveTo(-2, 0);
        ctx.quadraticCurveTo(-5 - wingFlap * 3, -3 + wingFlap * 2, -8, -1 + wingFlap);
        ctx.quadraticCurveTo(-7, 2, -3, 1);
        ctx.closePath();
        ctx.fill();

        // Right wing
        ctx.beginPath();
        ctx.moveTo(2, 0);
        ctx.quadraticCurveTo(5 + wingFlap * 3, -3 + wingFlap * 2, 8, -1 + wingFlap);
        ctx.quadraticCurveTo(7, 2, 3, 1);
        ctx.closePath();
        ctx.fill();

        ctx.restore();
    }

    private animate = (): void => {
        if (!this.ctx || !this.canvasRef()) return;

        const canvas = this.canvasRef().nativeElement;
        const ctx = this.ctx;

        this.time += 0.016; // ~60fps

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Update and draw bats
        const bats = this.bats();
        const updatedBats = bats.map(bat => {
            // Update position with sine wave movement
            bat.x += bat.speed;
            bat.y += Math.sin(bat.phase) * 0.5;
            bat.phase += bat.frequency;

            // Update wing flapping
            bat.wingPhase += bat.wingSpeed;

            // Wrap around screen
            if (bat.x > canvas.width + 20) {
                bat.x = -20;
                bat.y = Math.random() * canvas.height * 0.6;
            }

            this.drawBat(ctx, bat);
            return bat;
        });
        this.bats.set(updatedBats);

        // Update and draw embers (floating upward like fireflies/embers)
        const embers = this.embers();
        const updatedEmbers = embers.map(ember => {
            ember.x += ember.drift + Math.sin(this.time * 2 + ember.flicker) * 0.2;
            ember.y += ember.speed;
            ember.flicker += 0.05;

            // Reset if goes off screen
            if (ember.y < -10) {
                return this.createEmber(canvas);
            }

            if (ember.x > canvas.width + 10) {
                ember.x = -10;
            } else if (ember.x < -10) {
                ember.x = canvas.width + 10;
            }

            // Draw ember with warm glow
            ctx.save();
            const flickerOpacity = ember.opacity * (0.7 + Math.sin(ember.flicker) * 0.3);
            ctx.globalAlpha = flickerOpacity;

            const gradient = ctx.createRadialGradient(ember.x, ember.y, 0, ember.x, ember.y, ember.radius * 3);
            gradient.addColorStop(0, '#ff9a56');
            gradient.addColorStop(0.4, '#ff7733');
            gradient.addColorStop(1, 'rgba(255, 119, 51, 0)');

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(ember.x, ember.y, ember.radius * 3, 0, Math.PI * 2);
            ctx.fill();

            // Bright core
            ctx.globalAlpha = flickerOpacity * 1.2;
            ctx.fillStyle = '#ffb380';
            ctx.beginPath();
            ctx.arc(ember.x, ember.y, ember.radius * 0.5, 0, Math.PI * 2);
            ctx.fill();

            ctx.restore();

            return ember;
        });
        this.embers.set(updatedEmbers);

        this.animationFrameId = requestAnimationFrame(this.animate);
    };

    private cleanup(): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
        if (this.ctx && this.canvasRef()) {
            const canvas = this.canvasRef().nativeElement;
            this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }

    ngOnDestroy(): void {
        this.cleanup();
        const canvas = this.canvasRef()?.nativeElement;
        if (canvas) {
            window.removeEventListener('resize', () => this.resizeCanvas(canvas));
        }
    }
}
